import { useState, useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import {
    CheckCircle2, TrendingUp, Clock, Zap,
    ArrowRight, Lock, Mail, ExternalLink,
    Layers, ChevronDown, Sparkles, Loader2,
    Building2, BarChart3, Settings2, BookOpen,
    Brain, Calendar, FileSignature, Presentation,
    Globe, ShieldCheck, Wrench, Signal, FileText, Tag
} from "lucide-react";
import { toast } from "sonner";
import { saveLeadToFirestore } from "@/lib/firebase";
import GapAlertsPanel from "./GapAlertsPanel";
import MaturityRadarChart from "./MaturityRadarChart";
import ROIBenchmarkCard from "./ROIBenchmarkCard";


/* ── Category → icon map ────────────────────────────────────── */
const CATEGORY_ICONS = {
    "CRM": Building2,
    "Project Management": Layers,
    "Communication": Zap,
    "Document Management": BookOpen,
    "AI Layer": Brain,
    "Workflow Automation": Settings2,
    "Scheduling": Calendar,
    "Contracts & Proposals": FileSignature,
    "Client Reporting": BarChart3,
    "Meeting Intelligence": Presentation,
};

/* ── Priority badge styling ─────────────────────────────────── */
function PriorityBadge({ priority }) {
    const styles = {
        "Immediate": "border-white/25 text-zinc-300",
        "Month 2": "border-white/10 text-zinc-500",
        "Month 3+": "border-dashed border-white/8 text-zinc-600",
    };
    return (
        <span className={`inline-block text-[10px] font-normal uppercase tracking-wider border px-2 py-0.5 rounded ${styles[priority] || styles["Month 3+"]}`}>
            {priority}
        </span>
    );
}

/* =========================================================
   WEBSITE SCAN OVERVIEW PANEL
   ========================================================= */
function WebsiteOverview({ domain, title, detected_tools, industry_signals, pages_scanned, traffic_data }) {
    const [isOpen, setIsOpen] = useState(false);
    if (!domain) return null;

    const hasTier = traffic_data && traffic_data.traffic_tier && traffic_data.traffic_tier !== "Unknown";
    const tierColors = {
        "Very High": "text-emerald-400 border-emerald-500/20 bg-emerald-500/5",
        "High": "text-emerald-400 border-emerald-500/20 bg-emerald-500/5",
        "Medium-High": "text-sky-400 border-sky-500/20 bg-sky-500/5",
        "Medium": "text-sky-400 border-sky-500/20 bg-sky-500/5",
        "Low": "text-zinc-400 border-white/10 bg-white/3",
        "Very Low": "text-zinc-500 border-white/8 bg-white/2",
        "Unknown": "text-zinc-600 border-white/5 bg-white/2",
    };
    const tierClass = hasTier ? (tierColors[traffic_data.traffic_tier] || tierColors["Unknown"]) : "";

    return (
        <div className="mb-8 animate-fade-in opacity-0" style={{ animationFillMode: "forwards" }}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-xl matte-card hover:border-white/12 transition-colors duration-150 group"
            >
                <div className="flex items-center gap-3">
                    <Globe className="w-3.5 h-3.5 text-zinc-400 flex-shrink-0" />
                    <div className="text-left">
                        <span className="text-sm font-normal text-white">{domain}</span>
                        {title && (
                            <span className="text-xs text-zinc-600 ml-2 hidden sm:inline">— {title.length > 50 ? title.slice(0, 47) + "…" : title}</span>
                        )}
                    </div>
                    <div className="flex items-center gap-1.5 ml-1">
                        {pages_scanned && pages_scanned.length > 0 && (
                            <span className="info-pill text-[10px]">
                                <FileText className="w-2.5 h-2.5" />
                                {pages_scanned.length} page{pages_scanned.length !== 1 ? "s" : ""}
                            </span>
                        )}
                        {hasTier && (
                            <span className={`inline-flex items-center gap-1 text-[10px] font-normal border px-2 py-0.5 rounded ${tierClass}`}>
                                <Signal className="w-2.5 h-2.5" />
                                {traffic_data.traffic_tier} Traffic
                            </span>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wider text-zinc-600 group-hover:text-zinc-500 transition-colors hidden sm:block">
                        {isOpen ? "Hide" : "Scan Details"}
                    </span>
                    <ChevronDown className={`w-3.5 h-3.5 text-zinc-600 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                </div>
            </button>

            {isOpen && (
                <div className="mt-1 rounded-xl matte-card px-5 py-4 grid grid-cols-1 sm:grid-cols-3 gap-5 animate-fade-in opacity-0" style={{ animationFillMode: "forwards" }}>
                    {/* Tech stack detected */}
                    <div>
                        <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-600 mb-2">
                            <Wrench className="w-3 h-3" /> Tech Stack Detected
                        </p>
                        {detected_tools && detected_tools.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5">
                                {detected_tools.slice(0, 8).map((tool, i) => (
                                    <span key={i} className="text-[10px] font-normal border border-white/8 bg-white/3 text-zinc-400 px-2 py-0.5 rounded">
                                        {tool}
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <p className="text-xs text-zinc-600 italic">No known tools detected</p>
                        )}
                    </div>

                    {/* Industry signals */}
                    <div>
                        <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-600 mb-2">
                            <Tag className="w-3 h-3" /> Industry Signals
                        </p>
                        {industry_signals && industry_signals.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5">
                                {industry_signals.map((sig, i) => (
                                    <span key={i} className="text-[10px] font-normal border border-white/8 bg-white/3 text-zinc-400 px-2 py-0.5 rounded capitalize">
                                        {sig}
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <p className="text-xs text-zinc-600 italic">No signals detected</p>
                        )}
                    </div>

                    {/* Traffic intelligence */}
                    <div>
                        <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-600 mb-2">
                            <Signal className="w-3 h-3" /> Traffic Intelligence
                        </p>
                        {hasTier ? (
                            <div className="space-y-1">
                                <div className={`inline-flex items-center gap-1.5 text-xs font-normal border px-2.5 py-1 rounded ${tierClass}`}>
                                    {traffic_data.traffic_tier} Tier
                                    {traffic_data.estimated_monthly_visits && (
                                        <span className="text-[10px] opacity-70">· ~{traffic_data.estimated_monthly_visits}/mo</span>
                                    )}
                                </div>
                                {traffic_data.page_rank !== null && traffic_data.page_rank !== undefined && (
                                    <p className="text-[10px] text-zinc-600 mt-1">PageRank: {traffic_data.page_rank}/10</p>
                                )}
                                {traffic_data.global_rank && (
                                    <p className="text-[10px] text-zinc-600">Global rank: #{traffic_data.global_rank.toLocaleString()}</p>
                                )}
                            </div>
                        ) : (
                            <p className="text-xs text-zinc-600 italic">Traffic data unavailable</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

/* =========================================================
   A. EXECUTIVE SUMMARY
   ========================================================= */
function ExecutiveSummary({ blueprint, confidenceScore, automationMaturityScore, analysisSummary, traffic_data }) {
    const isBlueprint = !!blueprint;

    return (
        <div className="mb-10">
            {/* Niche label */}
            {isBlueprint && (
                <div className="flex items-center gap-2 mb-5">
                    <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-normal">Niche</span>
                    <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-300 font-normal border border-white/10 px-2.5 py-1 rounded">
                        {blueprint.niche_label}
                    </span>
                </div>
            )}

            {/* Stat row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">

                {isBlueprint && blueprint.roi_metrics.slice(0, 3).map((m, i) => (
                    <div key={i} className="matte-card rounded-xl p-4">
                        <p className="text-[10px] uppercase tracking-wider text-zinc-600 mb-1">{m.label}</p>
                        <p className="text-2xl font-light text-white">{m.value}</p>
                    </div>
                ))}
                {traffic_data && traffic_data.traffic_tier !== "Unknown" && (
                    <div className="matte-card rounded-xl p-4 border border-white/5">
                        <p className="text-[10px] uppercase tracking-wider text-zinc-600 mb-1 flex items-center gap-1.5">
                            <TrendingUp className="w-3 h-3" /> Traffic Intelligence
                        </p>
                        <p className="text-xl font-light text-white">{traffic_data.traffic_tier}</p>
                        <p className="text-[10px] text-zinc-500 mt-1">~{traffic_data.estimated_monthly_visits}/mo</p>
                    </div>
                )}
                {!isBlueprint && (!traffic_data || traffic_data.traffic_tier === "Unknown") && (
                    <div className="matte-card rounded-xl p-4 col-span-2 md:col-span-4">
                        <p className="text-[10px] uppercase tracking-wider text-zinc-600 mb-1.5 flex items-center gap-1.5">
                            <Sparkles className="w-3 h-3" /> Executive Insight
                        </p>
                        <p className="text-xs font-light text-zinc-400 leading-relaxed">{analysisSummary}</p>
                    </div>
                )}
                {!isBlueprint && traffic_data && traffic_data.traffic_tier !== "Unknown" && (
                    <div className="matte-card rounded-xl p-4 col-span-1 md:col-span-3">
                        <p className="text-[10px] uppercase tracking-wider text-zinc-600 mb-1.5 flex items-center gap-1.5">
                            <Sparkles className="w-3 h-3" /> Executive Insight
                        </p>
                        <p className="text-xs font-light text-zinc-400 leading-relaxed">{analysisSummary}</p>
                    </div>
                )}
            </div>

            {/* Summary paragraph & Micro-Proof (blueprint mode) */}
            {isBlueprint && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="matte-card rounded-xl p-5 md:col-span-2">
                        <p className="text-[10px] uppercase tracking-wider text-zinc-600 mb-2 flex items-center gap-1.5">
                            <Sparkles className="w-3 h-3" /> Executive Summary
                        </p>
                        <p className="text-sm font-light text-zinc-400 leading-relaxed">{blueprint.executive_summary}</p>
                    </div>

                    {/* Micro-Proof Layer */}
                    <div className="matte-card rounded-xl p-5 border border-emerald-500/10 bg-emerald-500/[0.02]">
                        <p className="text-[10px] uppercase tracking-wider text-emerald-500/80 mb-3 flex items-center gap-1.5">
                            <TrendingUp className="w-3 h-3" /> Recent Client Results
                        </p>
                        <div className="space-y-3">
                            <div>
                                <p className="text-xs font-medium text-white">Consulting Firm (12 people)</p>
                                <ul className="mt-1.5 space-y-1.5">
                                    <li className="text-[11px] font-light text-zinc-400 flex items-start gap-1.5">
                                        <span className="text-emerald-500/60 mt-0.5">→</span>
                                        Reduced proposal turnaround from 4 days to 1 day
                                    </li>
                                    <li className="text-[11px] font-light text-zinc-400 flex items-start gap-1.5">
                                        <span className="text-emerald-500/60 mt-0.5">→</span>
                                        Saved 18 admin hours/week
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/* =========================================================
   B/C/D. STACK LAYER SECTION
   ========================================================= */
function StackLayerSection({ layer }) {
    return (
        <div className="mb-10">
            <div className="flex items-center gap-3 mb-5">
                <div className="h-px flex-1 bg-white/5" />
                <h2 className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-normal whitespace-nowrap">{layer.layer}</h2>
                <div className="h-px flex-1 bg-white/5" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {layer.tools.map((tool, i) => {
                    const Icon = CATEGORY_ICONS[tool.category] || Zap;
                    return (
                        <div key={i} className="matte-card rounded-xl p-5 hover:border-white/12 transition-colors duration-200">
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-2.5">
                                    <div className="w-8 h-8 rounded-lg bg-white/4 border border-white/7 flex items-center justify-center flex-shrink-0">
                                        <Icon className="w-4 h-4 text-zinc-400" />
                                    </div>
                                    <div>
                                        <p className="text-[10px] uppercase tracking-wider text-zinc-600 font-normal">{tool.category}</p>
                                        <h3 className="text-sm font-normal text-white">{tool.name}</h3>
                                    </div>
                                </div>
                                <PriorityBadge priority={tool.priority} />
                            </div>

                            <p className="text-xs font-light text-zinc-400 leading-relaxed mb-4">{tool.justification}</p>

                            <div className="flex items-center justify-between">
                                <span className="text-[10px] font-normal text-zinc-600">{tool.pricing_tier}</span>
                                <a
                                    href={tool.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-[10px] text-zinc-500 hover:text-white transition-colors duration-150"
                                >
                                    Visit <ExternalLink className="w-2.5 h-2.5" />
                                </a>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/* =========================================================
   E. ROI & EFFICIENCY
   ========================================================= */
function ROISection({ metrics }) {
    return (
        <div className="mb-10">
            <div className="flex items-center gap-3 mb-5">
                <div className="h-px flex-1 bg-white/5" />
                <h2 className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-normal whitespace-nowrap">
                    E. Estimated ROI &amp; Efficiency Gains
                </h2>
                <div className="h-px flex-1 bg-white/5" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {metrics.map((m, i) => (
                    <div key={i} className="matte-card rounded-xl p-5">
                        <TrendingUp className="w-4 h-4 text-zinc-600 mb-3" />
                        <p className="text-xl font-light text-white mb-1">{m.value}</p>
                        <p className="text-xs font-normal text-zinc-400 mb-1">{m.label}</p>
                        <p className="text-[10px] font-light text-zinc-600 leading-relaxed">{m.description}</p>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* =========================================================
   F. IMPLEMENTATION ROADMAP (blurred lead gate)
   ========================================================= */
function RoadmapSection({ roadmap, onUnlock }) {
    const [email, setEmail] = useState("");
    const [website, setWebsite] = useState("");
    const [loading, setLoading] = useState(false);
    const [unlocked, setUnlocked] = useState(false);
    const recaptchaRef = useRef(null);

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Honeypot trap for bots
        const formData = new FormData(e.target);
        if (formData.get("company_website_url_check")) {
            return;
        }

        // Require visible CAPTCHA to be completed
        const token = recaptchaRef.current ? recaptchaRef.current.getValue() : null;
        if (!token) {
            // gently scroll the captcha into view so user sees it
            recaptchaRef.current?.getWidgetId && window.scrollTo({ behavior: "smooth" });
            return;
        }

        setLoading(true);
        await onUnlock(email, website, token);
        setLoading(false);
        setUnlocked(true);
    };

    return (
        <div className="mb-10">
            <div className="flex items-center gap-3 mb-5">
                <div className="h-px flex-1 bg-white/5" />
                <h2 className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-normal whitespace-nowrap">
                    F. Implementation Roadmap
                </h2>
                <div className="h-px flex-1 bg-white/5" />
            </div>

            <div className="relative rounded-xl overflow-hidden matte-card">
                {/* Phase timeline */}
                <div className={`grid grid-cols-1 md:grid-cols-3 gap-0 divide-y md:divide-y-0 md:divide-x divide-white/5 ${!unlocked ? "blur-[3px] select-none pointer-events-none" : ""}`}>
                    {roadmap.map((phase, i) => (
                        <div key={i} className="p-6">
                            <h3 className="text-sm font-normal text-white mb-3 mt-2">{phase.title}</h3>
                            <ul className="space-y-2">
                                {phase.actions.map((action, j) => (
                                    <li key={j} className="flex items-start gap-2">
                                        <CheckCircle2 className="w-3 h-3 text-zinc-600 flex-shrink-0 mt-0.5" />
                                        <span className="text-xs font-light text-zinc-400 leading-relaxed">{action}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                {/* Lock overlay */}
                {!unlocked && (
                    <div className="absolute inset-0 bg-gradient-to-t from-[#18181b] via-[#18181b]/90 to-transparent flex flex-col items-center justify-center p-6 text-center">
                        <div className="w-full max-w-sm">
                            <div className="flex items-center justify-center gap-1.5 mb-3">
                                <Lock className="w-3 h-3 text-zinc-500" />
                                <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Locked</span>
                            </div>
                            <h3 className="text-lg font-light text-white mb-1 tracking-tight">Unlock Your Roadmap</h3>
                            <p className="text-xs font-light text-zinc-500 mb-5">
                                Enter your email to reveal the full implementation plan.
                            </p>
                            <form onSubmit={handleSubmit} className="space-y-3">
                                <input
                                    type="text"
                                    name="company_website_url_check"
                                    style={{ display: 'none' }}
                                    tabIndex="-1"
                                    autoComplete="off"
                                />
                                <input
                                    type="email"
                                    required
                                    placeholder="work@yourfirm.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="flat-input w-full h-10 px-4 text-sm font-light"
                                />
                                <ReCAPTCHA
                                    ref={recaptchaRef}
                                    size="normal"
                                    theme="dark"
                                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"}
                                    className="mt-1 mb-1"
                                />
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="flat-btn-primary w-full font-medium justify-center"
                                >
                                    {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                                    Unlock Roadmap →
                                </button>
                            </form>
                            <div className="mt-4 flex flex-col items-center gap-2">
                                <p className="text-[10px] text-zinc-700">No spam. Unsubscribe anytime.</p>
                                <a
                                    href="/sample-blueprint.pdf"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[10px] text-zinc-500 hover:text-white transition-colors flex items-center gap-1"
                                >
                                    Not sure? <span className="underline decoration-white/20 underline-offset-2">See a sample blueprint</span> <ExternalLink className="w-2.5 h-2.5" />
                                </a>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

/* =========================================================
   SCRIBD-STYLE LEAD GATE (legacy / general mode)
   =========================================================
   Blurs "full report" bonus content below the 3 tool cards.
   An email capture form sits pinned on the gradient — just
   like Scribd's document paywall.
   ========================================================= */
function ScribdGate({ onUnlock }) {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [unlocked, setUnlocked] = useState(false);
    const recaptchaRef = useRef(null);

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Require visible CAPTCHA to be completed
        const token = recaptchaRef.current ? recaptchaRef.current.getValue() : null;
        if (!token) {
            toast.error("Please complete the reCAPTCHA check.");
            return;
        }

        setLoading(true);
        await onUnlock(email, "", token);
        setLoading(false);
        setUnlocked(true);
    };

    // Fake "full report" content that sits behind the blur
    const fakeRows = [
        { label: "ROI Projection (Month 1)", value: "+$4,200 saved" },
        { label: "ROI Projection (Month 3)", value: "+$14,800 saved" },
        { label: "Implementation Timeline", value: "3–6 weeks" },
        { label: "Integration complexity", value: "Low — no-code" },
        { label: "Foundation checklist", value: "9 steps" },
        { label: "Automation flows", value: "4 Zapier zaps" },
        { label: "Analytics setup", value: "2 dashboards" },
        { label: "Total tools in full stack", value: "8 tools" },
    ];

    return (
        <div className="relative mt-2 rounded-xl overflow-hidden">
            {/* Blurred bonus content */}
            <div className={`matte-card rounded-xl overflow-hidden ${!unlocked ? "pointer-events-none select-none" : ""}`}>
                <div className={`p-5 ${!unlocked ? "blur-[5px]" : ""}`}>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-600 mb-4">Full Automation Report</p>
                    <div className="space-y-2">
                        {fakeRows.map((row, i) => (
                            <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                                <span className="text-xs font-light text-zinc-400">{row.label}</span>
                                <span className="text-xs font-normal text-white">{row.value}</span>
                            </div>
                        ))}
                    </div>
                    <div className="mt-5 grid grid-cols-3 gap-3">
                        {[1, 2, 3].map((_, i) => (
                            <div key={i} className="rounded-lg bg-white/3 border border-white/6 p-3">

                                <div className="space-y-1.5">
                                    {[...Array(3)].map((_, j) => (
                                        <div key={j} className="h-2 rounded bg-white/8" style={{ width: `${60 + j * 15}%` }} />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Gradient fade + email gate overlay */}
            {!unlocked && (
                <div className="absolute inset-0 flex flex-col justify-end" style={{
                    background: "linear-gradient(to bottom, transparent 0%, rgba(9,9,11,0.7) 40%, rgba(9,9,11,0.97) 100%)"
                }}>
                    <div className="p-6 pb-7">
                        <div className="max-w-sm mx-auto text-center">
                            <Lock className="w-4 h-4 text-zinc-500 mx-auto mb-2" />
                            <h3 className="text-base font-light text-white tracking-tight mb-1">
                                Get the Full Report
                            </h3>
                            <p className="text-xs font-light text-zinc-500 mb-4">
                                ROI projections, implementation steps, and the full integration map — free.
                            </p>
                            <form onSubmit={handleSubmit} className="flex flex-col gap-3 items-center">
                                <div className="flex gap-2 w-full">
                                    <input
                                        type="email"
                                        required
                                        placeholder="work@email.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="flat-input flex-1 h-10 px-3 text-sm font-light"
                                    />
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="flat-btn-primary px-4 h-10 font-medium whitespace-nowrap"
                                    >
                                        {loading
                                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                            : "Unlock →"}
                                    </button>
                                </div>
                                <ReCAPTCHA
                                    ref={recaptchaRef}
                                    size="normal"
                                    theme="dark"
                                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LeXFHQsAAAAAOIegMtmLnFE7MD9FyuqNKCh5gg1"}
                                />
                            </form>
                            <p className="text-[10px] text-zinc-700 mt-2">No spam. Unsubscribe anytime.</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/* =========================================================
   LEGACY: generic tool cards (non-niche fallback)
   ========================================================= */
function LegacyToolCard({ tool, index }) {
    return (
        <div
            className="matte-card rounded-xl p-5 hover:border-white/12 transition-colors duration-200 animate-fade-in-up opacity-0"
            style={{ animationDelay: `${index * 80}ms`, animationFillMode: "forwards" }}
        >
            <div className="flex items-start justify-between mb-3">
                <h3 className="text-sm font-normal text-white">{tool.name}</h3>
                <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/redirect/${tool.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[10px] text-zinc-500 hover:text-white transition-colors"
                >
                    Visit <ExternalLink className="w-2.5 h-2.5" />
                </a>
            </div>
            <p className="text-xs font-light text-zinc-400 leading-relaxed mb-3">{tool.description}</p>
            {tool.why_it_fits && (
                <p className="text-[10px] font-light text-zinc-500 leading-relaxed border-t border-white/5 pt-2">{tool.why_it_fits}</p>
            )}
        </div>
    );
}

/* =========================================================
   A2. DEEP AUDIT PDF
   ========================================================= */
function AuditPDFCard({ industry, location, url }) {
    const [downloading, setDownloading] = useState(false);

    const handleDownload = async () => {
        if (!location || !industry) {
            toast.error("Location and Industry are required for the deep audit.");
            return;
        }
        
        setDownloading(true);
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
            const response = await fetch(`${backendUrl}/api/business-audit`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    business_type: industry,
                    location: location,
                }),
            });

            if (!response.ok) throw new Error("Audit generation failed");

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = downloadUrl;
            a.download = `yukti_audit_${industry.replace(/\s+/g, "_").toLowerCase()}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(downloadUrl);
            toast.success("Executive Audit downloaded successfully.");
        } catch (error) {
            console.error(error);
            toast.error("Failed to generate the Executive Audit PDF.");
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div className="mb-10 animate-fade-in opacity-0" style={{ animationFillMode: "forwards" }}>
            <div className="matte-card rounded-xl p-6 border border-white/5 relative overflow-hidden group">
                {/* Background glow */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative flex flex-col md:flex-row items-center justify-between gap-6">
                    <div>
                        <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-blue-400 mb-2">
                            <FileSignature className="w-3 h-3" /> Deep Local Analysis
                        </p>
                        <h3 className="text-lg font-light text-white tracking-tight mb-2">
                            Download your McKinsey-Grade Executive Audit
                        </h3>
                        <p className="text-xs font-light text-zinc-400 leading-relaxed max-w-xl">
                            We've cross-referenced your profile with ABS data, local competitor densities, and recent news in <span className="text-zinc-200">{location || "your area"}</span>. Get the comprehensive PDF report with revenue recovery benchmarks and a strict 30-day execution sprint.
                        </p>
                    </div>
                    
                    <button
                        onClick={handleDownload}
                        disabled={downloading || !location}
                        className="flat-btn-primary whitespace-nowrap min-w-[200px] justify-center shadow-lg shadow-blue-500/10"
                    >
                        {downloading ? (
                            <>
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                Generating (Takes ~10s)…
                            </>
                        ) : (
                            <>
                                <FileText className="w-3.5 h-3.5" />
                                Download Deep Audit
                            </>
                        )}
                    </button>
                </div>
                {!location && (
                    <p className="text-[10px] text-zinc-500 mt-3 relative">
                        * Location was not provided in the wizard. Required for deep audit.
                    </p>
                )}
            </div>
        </div>
    );
}

/* =========================================================
   MAIN EXPORT
   ========================================================= */
export function ReportView({ results, onReset }) {
    const { tools, analysisSummary, confidenceScore, automation_maturity_score, blueprint, teamSize, painPoint, traffic_data,
        scanned_domain, scanned_title, detected_tools, industry_signals, pages_scanned,
        scan_gaps, tech_debt_flags, yami_breakdown } = results;
    const isBlueprint = !!blueprint;
    const [isHighTicket, setIsHighTicket] = useState(false);
    const [hasUnlocked, setHasUnlocked] = useState(false);

    const handleUnlock = async (email, website, recaptchaToken) => {
        try {
            const toolNames = isBlueprint
                ? blueprint.stack.flatMap(l => l.tools.map(t => t.name))
                : tools.map(t => t.name);

            await saveLeadToFirestore({
                email, website,
                industry: blueprint?.niche_label || "Unknown",
                recommended_tools: toolNames,
            });

            const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
            const res = await fetch(`${backendUrl}/api/capture-email`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    email, website,
                    industry: blueprint?.niche_label || "Unknown",
                    recommended_tools: toolNames,
                    team_size: teamSize || null,
                    pain_point: painPoint || null,
                    recaptcha_token: recaptchaToken,
                }),
            });
            const data = await res.json();
            results.capturedEmail = email; // Populate state for Calendly URL
            if (data.is_high_ticket) setIsHighTicket(true);
            setHasUnlocked(true);
            toast.success("Blueprint sent to your email!");
        } catch (err) {
            console.error(err);
            results.capturedEmail = email; // Fallback
            setHasUnlocked(true);
            toast.success("Blueprint sent to your email!");
        }
    };

    return (
        <div className="min-h-screen text-white py-10 px-4 md:px-8">
            <div className="max-w-5xl mx-auto">

                {/* Report header */}
                <div className="flex items-center justify-between mb-10">
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-600 mb-1">
                            YuktiAI — Automation Blueprint
                        </p>
                        <h1 className="text-2xl font-light text-white tracking-tight">
                            {isBlueprint ? "Consulting Stack Blueprint" : "AI Stack Recommendations"}
                        </h1>
                    </div>
                    <button
                        onClick={onReset}
                        className="text-xs font-light text-zinc-500 hover:text-white transition-colors flex items-center gap-1.5"
                    >
                        Start New Audit <ArrowRight className="w-3 h-3" />
                    </button>
                </div>

                {/* Website Scan Overview Panel */}
                <WebsiteOverview
                    domain={scanned_domain}
                    title={scanned_title}
                    detected_tools={detected_tools}
                    industry_signals={industry_signals}
                    pages_scanned={pages_scanned}
                    traffic_data={traffic_data}
                />

                {/* Gap Alerts Panel (Layer 1) — surface critical missing tools */}
                {(scan_gaps?.length > 0 || tech_debt_flags?.length > 0) && (
                    <GapAlertsPanel
                        gaps={scan_gaps || []}
                        techDebtFlags={tech_debt_flags || []}
                    />
                )}

                {/* YAMI Maturity Breakdown (Layer 2) — only when tools detected */}
                {yami_breakdown && yami_breakdown.yami_score !== undefined && (
                    <MaturityRadarChart breakdown={yami_breakdown} />
                )}


                {/* A. Executive Summary */}
                <ExecutiveSummary
                    blueprint={blueprint}
                    confidenceScore={confidenceScore}
                    automationMaturityScore={automation_maturity_score}
                    analysisSummary={analysisSummary}
                    traffic_data={traffic_data}
                />

                {/* A2. Deep Audit PDF */}
                <AuditPDFCard 
                    industry={results.industry || blueprint?.niche_label || "Business"} 
                    location={results.location || ""} 
                    url={scanned_domain} 
                />

                {isBlueprint ? (
                    <>
                        {/* B / C / D — Stack Layers */}
                        {blueprint.stack.map((layer, i) => (
                            <StackLayerSection key={i} layer={layer} />
                        ))}

                        {/* E — ROI */}
                        <ROISection metrics={blueprint.roi_metrics} />

                        {/* E2 — Grounded ROI Benchmarks (Layer 3) */}
                        <ROIBenchmarkCard
                            roiProjection={blueprint.roi_projection}
                            roiBenchmarks={results.roi_benchmarks || []}
                        />

                        {/* F — Roadmap (gated) */}
                        <RoadmapSection roadmap={blueprint.roadmap} onUnlock={handleUnlock} />
                    </>
                ) : (
                    <>
                        {/* Legacy generic tool grid */}
                        <div className="mb-5">
                            <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-600 mb-1">B. Recommended Stack</p>
                            <h2 className="text-lg font-light text-white">Your Custom AI Stack</h2>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
                            {tools.map((tool, i) => (
                                <LegacyToolCard key={tool.id} tool={tool} index={i} />
                            ))}
                        </div>

                        {/* Scribd-style lead gate */}
                        {tools.length > 0 && (
                            <ScribdGate onUnlock={handleUnlock} />
                        )}
                    </>
                )}

                {/* Post-unlock success + high-ticket CTA */}
                {hasUnlocked && (
                    <div className="mt-6 animate-fade-in opacity-0" style={{ animationFillMode: "forwards" }}>
                        <div className="matte-card rounded-xl p-5 flex items-center gap-3 mb-4">
                            <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                            <p className="text-sm font-light text-zinc-300">
                                Your full Automation Blueprint has been sent to your email. Open it for your 30-day plan, ROI projections, and implementation guide.
                            </p>
                        </div>
                        {isHighTicket && (
                            <div className="matte-card rounded-xl p-6 border border-white/10">
                                <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-600 mb-2">🔥 Private Advisory Access</p>
                                <h3 className="text-base font-light text-white tracking-tight mb-1">
                                    Firms at your scale benefit from structured deployment.
                                </h3>
                                <p className="text-xs font-light text-zinc-500 mb-4">
                                    We've opened a limited advisory slot for your blueprint. We map, build, and deploy the stack for you in 10 days.
                                </p>
                                <a
                                    href={`https://calendly.com/yuktiai/strategy?email=${encodeURIComponent(results.capturedEmail || "")}&name=${encodeURIComponent((results.capturedEmail || "").split("@")[0] || "")}&a1=${encodeURIComponent(painPoint || "")}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flat-btn-primary inline-flex font-medium"
                                >
                                    Secure Advisory Slot →
                                </a>
                            </div>
                        )}
                    </div>
                )}

                {/* Founder Presence */}
                {hasUnlocked && (
                    <div className="mt-12 pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4 animate-fade-in opacity-0" style={{ animationFillMode: "forwards", animationDelay: "200ms" }}>
                        <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-zinc-400 font-light text-sm">
                                KG
                            </div>
                            <div>
                                <p className="text-sm font-medium text-white tracking-tight">Kishan Gowda</p>
                                <p className="text-[10px] uppercase tracking-wider text-zinc-500">AI Systems & Automation Advisory</p>
                            </div>
                        </div>

                    </div>
                )}

                {/* Footer */}
                <div className="mt-12 pt-8 border-t border-white/5 flex justify-between items-center">
                    <p className="text-[10px] font-light text-zinc-700">Generated by YuktiAI</p>
                    <button
                        onClick={onReset}
                        className="text-[10px] font-light text-zinc-600 hover:text-white transition-colors"
                    >
                        ← New Analysis
                    </button>
                </div>
            </div>
        </div>
    );
}
